import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import { writeEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/writeEditorContentFragment.ts';
import type { EditorContentFragmentWriteArgs } from '@confluence/editor-content-unified-query/entry-points/writeEditorContentFragment.ts';

import type { useContentModeFragment_content$key } from './__generated__/useContentModeFragment_content.graphql.ts';
import type { useEditorContentModeValueQuery$data } from './__generated__/useEditorContentModeValueQuery.graphql.ts';

// useEditorContentModeValueQuery's selection for EditorContentUnifiedQuery, with isDraft as
// !isLivePage, as it is on the editor routes; same fields, so both share the store records.
const contentModeFragmentNode: GraphQLTaggedNode = graphql`
	fragment useContentModeFragment_content on Content
	@argumentDefinitions(isLivePage: { type: "Boolean!" })
	@catch {
		id
		contentProperties {
			latest @include(if: $isLivePage) {
				contentMode
			}
			draft @skip(if: $isLivePage) {
				contentMode
			}
		}
	}
`;

/**
 * The content mode from EditorContentUnifiedQuery in the shape of useEditorContentModeValueQuery
 * data. When the fragment is provided, its reader skips useEditorContentModeValueQuery.
 */
export function useContentModeFragment({
	contentId,
}: {
	contentId: string | undefined;
}): EditorContentFragmentResult<useEditorContentModeValueQuery$data> {
	return useEditorContentFragment<
		useContentModeFragment_content$key,
		useEditorContentModeValueQuery$data
	>('contentModeFragment', contentModeFragmentNode, {
		contentId,
		mapData: (value) => ({ content: { nodes: [value] } }),
	});
}

/** Writes the content mode the fragment reads on this route: latest on Live Page, draft otherwise. */
export function writeContentModeFragment(
	args: EditorContentFragmentWriteArgs,
	{ contentMode }: { contentMode: string },
): void {
	writeEditorContentFragment<useContentModeFragment_content$key>(
		contentModeFragmentNode,
		args,
		args.isLivePage
			? { contentProperties: { latest: { contentMode } } }
			: { contentProperties: { draft: { contentMode } } },
	);
}
